import type { HTMLAttributes, MouseEvent, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from "react";
import { cn } from "../lib/cn";
import { Icon } from "./icon";
import { Skeleton } from "./states";

export type SortDirection = "asc" | "desc" | false;

/**
 * `.table` inside a horizontal scroller. `stickyHeader` pins the thead inside a max-height scroller.
 * `scrollRegion` makes the scroller a labelled, keyboard-focusable region (axe
 * `scrollable-region-focusable`) for tables that overflow on narrow screens and hold no focusable cells.
 */
export function Table({
  className,
  stickyHeader = false,
  scrollRegion = false,
  containerClassName,
  ...rest
}: HTMLAttributes<HTMLTableElement> & { stickyHeader?: boolean; scrollRegion?: boolean; containerClassName?: string }) {
  const region = scrollRegion
    ? { role: "region", tabIndex: 0, "aria-label": rest["aria-label"], "aria-labelledby": rest["aria-labelledby"] }
    : {};
  return (
    <div
      className={cn("relative overflow-x-auto", scrollRegion && "focus-visible:outline-2 focus-visible:outline-primary", stickyHeader && "max-h-[560px] overflow-y-auto", containerClassName)}
      {...region}
    >
      <table className={cn("table", stickyHeader && "[&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10", className)} {...rest} />
    </div>
  );
}

export interface SortableHeaderProps extends Omit<ThHTMLAttributes<HTMLTableCellElement>, "onClick"> {
  label: ReactNode;
  direction: SortDirection;
  onSort: () => void;
  align?: "left" | "right";
}

const ARIA_SORT = { asc: "ascending", desc: "descending" } as const;

/** Header cell with aria-sort and a real button; arrow icon shows direction. */
export function SortableHeader({ label, direction, onSort, align = "left", className, ...rest }: SortableHeaderProps) {
  return (
    <th aria-sort={direction ? ARIA_SORT[direction] : "none"} className={cn(align === "right" && "text-right", className)} {...rest}>
      <button
        type="button"
        onClick={onSort}
        className={cn("inline-flex cursor-pointer items-center gap-1 uppercase hover:text-fg", align === "right" && "flex-row-reverse")}
      >
        {label}
        <Icon
          name={direction === "asc" ? "chevron-up" : direction === "desc" ? "chevron-down" : "chevrons-up-down"}
          className={cn("size-3", !direction && "opacity-50")}
        />
      </button>
    </th>
  );
}

const INTERACTIVE = "a,button,input,select,label,textarea,[role=switch],[role=checkbox],[role=menuitem]";

/**
 * `tr[data-href]` row link: whole-row click navigates, ignoring clicks on inner controls.
 * Keyboard users reach the destination through the in-row primary link (`TableRowLink` requires one).
 */
export function TableRowLink({
  onNavigate,
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLTableRowElement> & { onNavigate: () => void; children: ReactNode }) {
  const onClick = (event: MouseEvent<HTMLTableRowElement>) => {
    if (event.target instanceof Element && event.target.closest(INTERACTIVE)) return;
    if (window.getSelection()?.toString()) return;
    onNavigate();
  };
  return (
    // Mouse convenience only; the row's primary cell holds a real link for keyboard / AT users.
    <tr className={cn("cursor-pointer", className)} onClick={onClick} {...rest}>
      {children}
    </tr>
  );
}

export function TableEmptyRow({ colSpan, children, ...rest }: TdHTMLAttributes<HTMLTableCellElement> & { colSpan: number }) {
  return (
    <tr>
      <td colSpan={colSpan} className="p-0" {...rest}>
        {children}
      </td>
    </tr>
  );
}

/** Skeleton rows matching the real column count (loading state — never a spinner). */
export function TableSkeletonRows({ rows = 5, columns }: { rows?: number; columns: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, r) => (
        <tr key={r} aria-hidden="true">
          {Array.from({ length: columns }, (_, c) => (
            <td key={c}>
              <Skeleton className={cn("h-4", c === 0 ? "w-40" : "w-20")} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
